/**
 * PATH: packages/ui/src/lib/nav-registry/registry.ts
 * FILE_ID: UI-NAV-REGISTRY-DATA-001
 * PURPOSE: The canonical ordered navigation registry — the UNION of every
 *          hub that appears in any shell's rail (master + newmark + sam +
 *          steel), with one canonical label / route / icon / gating per hub.
 * WHY:     Single source of truth so a hub's label + order + flag gate are
 *          defined ONCE, not re-typed (and drifted) in four nav files.
 *
 * Ordering: declaration order IS the canonical rail order. Tenant override
 * files pick a subset + their own order; the master shell uses this order.
 *
 * enableFlag mapping (Phase 0 verified — see plan §"Phase 0"):
 *   Only the unambiguous top-level module hubs carry a flag. Finance
 *   sub-tools (Bills, Ledger, Reconciliation, Smart Inspect) stay
 *   permission-gated only (core) to avoid regressing tenants whose
 *   module flag defaults `false` but who use the tool today. Hubs with
 *   no flag are core/always-on. The resolver fail-opens on an undefined
 *   or missing flag, so a transient bootstrap miss never blanks the rail.
 */

import {
  BarChart3,
  Banknote,
  BookOpen,
  Briefcase,
  Calculator,
  Calendar,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Code2,
  DollarSign,
  FileText,
  FolderOpen,
  GraduationCap,
  Hammer,
  HardHat,
  Inbox,
  KanbanSquare,
  LayoutDashboard,
  LineChart,
  Link2,
  Mail,
  MessageSquare,
  Package,
  Receipt,
  Scale,
  Settings,
  ShieldCheck,
  Sparkles,
  Truck,
  Users,
} from 'lucide-react'
import type { HubDef } from './types'

/**
 * RBAC permission codes (kept in lockstep with the tenant shells'
 * shared/services/rbac.ts PERMISSIONS). Declared here so the registry is
 * the single place
 * a hub's permission is stated. Tenant rbac.ts re-imports these in the
 * contract phase.
 */
export const NAV_PERMISSIONS = {
  MANAGE_FILES: 'manage_files',
  RUN_INSPECTIONS: 'run_inspections',
  VIEW_LEDGER: 'view_ledger',
  VIEW_CFO: 'view_cfo',
} as const

/**
 * Canonical ordered registry. `main` group first, then `tools`, matching
 * the historical MAIN_NAV / TOOLS_NAV split.
 */
export const NAV_REGISTRY: HubDef[] = [
  // ---- main ----------------------------------------------------------
  { id: 'dashboard', href: '/', label: 'Dashboard', shortLabel: 'Home', icon: LayoutDashboard, end: true, module: 'dashboard', group: 'main' },
  { id: 'jobs', href: '/jobs', label: 'Jobs', icon: Briefcase, module: 'jobs', group: 'main' },
  { id: 'email', href: '/email/inbox', label: 'Email Hub', shortLabel: 'Email', icon: Mail, enableFlag: 'enable_emails', module: 'email', group: 'main' },
  { id: 'kanban', href: '/kanban', label: 'Kanban', shortLabel: 'Kanban', icon: KanbanSquare, module: 'kanban', group: 'main' },
  { id: 'calendar', href: '/calendar', label: 'Calendar', icon: Calendar, module: 'calendar', group: 'main' },
  { id: 'bills', href: '/bills', label: 'Bills', icon: Receipt, module: 'billing', group: 'main' },
  { id: 'invoices', href: '/invoices', label: 'Invoices', icon: Receipt, module: 'invoices', group: 'main' },
  { id: 'crm', href: '/quotes', label: 'CRM', shortLabel: 'CRM', icon: FileText, end: true, module: 'crm', group: 'main' },
  { id: 'quote-builder', href: '/quotes/builder', label: 'Quote Builder', shortLabel: 'Builder', icon: Calculator, enableFlag: 'enable_quote_builder', module: 'quote', group: 'main' },
  { id: 'qdrive', href: '/qdrive', label: 'QDrive', icon: FolderOpen, enableFlag: 'enable_qdrive', module: 'qdrive', permission: NAV_PERMISSIONS.MANAGE_FILES, group: 'main' },
  { id: 'ai', href: '/ai', label: 'AI Hub', shortLabel: 'AI', icon: Sparkles, enableFlag: 'enable_ai_hub', module: 'ai', group: 'main' },
  { id: 'developer', href: '/developer', label: 'Developer Hub', shortLabel: 'Developer', icon: Code2, external: 'https://dashboard.quotech.ai/developer', module: 'developer', group: 'main' },
  { id: 'qa', href: '/qa', label: 'QA Hub', shortLabel: 'QA', icon: ShieldCheck, external: 'https://qa.quotech.ai', enableFlag: 'enable_quality_assurance', module: 'qa', group: 'main' },
  { id: 'teams', href: '/teams', label: 'Teams Hub', shortLabel: 'Teams', icon: MessageSquare, module: 'teams', group: 'main' },
  { id: 'training', href: '/training', label: 'Training', icon: GraduationCap, module: 'training', group: 'main' },
  { id: 'pricelist', href: '/pricelist', label: 'Pricelist', icon: ClipboardList, module: 'pricelist', group: 'main' },
  { id: 'material-pricing', href: '/material-pricing', label: 'Material Pricing', shortLabel: 'Pricing', icon: LineChart, enableFlag: 'enable_subcontractor_hub', module: 'pricelist', group: 'main' },
  { id: 'catalogue', href: '/catalogue', label: 'Catalogue & Stock', shortLabel: 'Stock', icon: Package, enableFlag: 'enable_subcontractor_hub', module: 'inventory', group: 'main' },
  { id: 'suppliers', href: '/suppliers', label: 'Suppliers', icon: Truck, enableFlag: 'enable_subcontractor_hub', module: 'suppliers', group: 'main' },
  { id: 'subcontracts', href: '/subcontracts', label: 'Subcontractors', shortLabel: 'Subbies', icon: HardHat, enableFlag: 'enable_subcontractor_hub', module: 'subcontracts', group: 'main' },
  { id: 'subcontracts-leveling', href: '/subcontracts/leveling', label: 'Leveling & Award', shortLabel: 'Leveling', icon: Scale, end: true, enableFlag: 'enable_subcontractor_hub', module: 'subcontracts', group: 'main' },
  { id: 'subcontracts-claims', href: '/subcontracts/claims', label: 'Claim Verification', shortLabel: 'Claims', icon: ClipboardCheck, end: true, enableFlag: 'enable_subcontractor_hub', module: 'subcontracts', group: 'main' },
  { id: 'subcontracts-retention', href: '/subcontracts/retention', label: 'Retention', shortLabel: 'Retention', icon: Banknote, end: true, enableFlag: 'enable_subcontractor_hub', module: 'subcontracts', group: 'main' },
  { id: 'contacts', href: '/clients', label: 'Contacts', icon: Users, module: 'crm', group: 'main' },
  { id: 'people', href: '/people', label: 'People', icon: Users, module: 'people', group: 'main' },

  // ---- tools ---------------------------------------------------------
  { id: 'workforce', href: '/workforce', label: 'Workforce Hub', shortLabel: 'Workforce', icon: Hammer, enableFlag: 'enable_workforce_hub', module: 'workforce', group: 'tools' },
  { id: 'payroll', href: '/payroll', label: 'Payroll', icon: DollarSign, module: 'payroll', group: 'tools' },
  { id: 'schedule', href: '/schedule', label: 'Schedule', icon: Calendar, module: 'workforce', group: 'tools' },
  { id: 'timesheets', href: '/timesheets', label: 'Timesheets', icon: Clock, module: 'workforce', group: 'tools' },
  { id: 'reports', href: '/reports', label: 'Reports', icon: BarChart3, module: 'reports', group: 'tools' },
  { id: 'smart-inspect', href: '/smart-inspect', label: 'Smart Inspect', icon: ClipboardCheck, permission: NAV_PERMISSIONS.RUN_INSPECTIONS, module: 'qa', group: 'tools' },
  { id: 'ledger', href: '/ledger', label: 'Ledger', icon: BookOpen, permission: NAV_PERMISSIONS.VIEW_LEDGER, module: 'cfo', group: 'tools' },
  { id: 'reconciliation', href: '/reconciliation', label: 'Reconciliation', shortLabel: 'Recon', icon: Inbox, permission: NAV_PERMISSIONS.VIEW_LEDGER, module: 'cfo', group: 'tools' },
  { id: 'cfo-job-linking', href: '/cfo-job-linking', label: 'CFO Job Linking', shortLabel: 'CFO', icon: Link2, enableFlag: 'enable_cfo_module', permission: NAV_PERMISSIONS.VIEW_CFO, module: 'cfo', group: 'tools' },
  { id: 'settings', href: '/settings', label: 'Settings', icon: Settings, module: 'settings', group: 'tools' },
]

/** Fast id -> HubDef lookup. */
export const NAV_REGISTRY_BY_ID: Record<string, HubDef> = Object.fromEntries(
  NAV_REGISTRY.map((hub) => [hub.id, hub]),
)
